import {isPlatformBrowser} from "@angular/common";

/** The settings shape this reads — any dashboard settings response carries it. */
export interface PortalRedirectableSettings {
  portalRedirectUrl?: string;
}

export class PortalRedirectUtils {
  /**
   * Sends the visitor to the agency's portal when the backend says the dashboard should not be
   * rendered at all, and reports whether it did.
   *
   * The backend decides both *whether* and *where* — this only follows the URL it produced, so the
   * client needs no notion of portals, hosts or UTM tagging.
   *
   * Callers must return `NEVER` when this returns true, so route activation never completes. That is
   * what stops the dashboard component from being constructed while the browser is leaving, and it is
   * the same shape `ClaimedPropertyAndContactResolver` already relies on.
   *
   * `window.location.href` rather than the router: the portal is a different origin, so this is a real
   * navigation, not an in-app one.
   *
   * Browser-only for now. On the server there is no way to emit a redirect from a resolver without
   * threading the express `RESPONSE` token into the render, so an SSR'd document renders the dashboard
   * and the browser leaves once Angular has bootstrapped — a brief flash on a hard load.
   * `TransferState` means that check costs no round trip, so it is bounded by bootstrap time; removing
   * it entirely belongs to the deferred server-side hop.
   */
  public static redirectIfNeeded(
    settings: PortalRedirectableSettings | null | undefined,
    // `Object`, matching Angular's own signature for PLATFORM_ID and the injection sites in the
    // resolvers: its values are strings, which `object` would reject.
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId: Object,
    // Seam for tests only — assigning the real `window.location.href` in a spec would navigate the
    // runner. Evaluated lazily, so it is never touched on the server.
    navigate: (url: string) => void = url => (window.location.href = url)
  ): boolean {
    const target = settings?.portalRedirectUrl;
    if (!target || !isPlatformBrowser(platformId)) {
      return false;
    }
    navigate(target);
    return true;
  }
}
